import React, { useEffect, useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";
import LineParam from "./LineParam";
import ScenePrint from "../Three/ScenePrint";
import LAD from "../Vessels/LAD/LAD";
import RCA from "../Vessels/RCA/RCA";
import LCX from "../Vessels/LCX/LCX";
import { useSelector } from "react-redux";
import lang from '../../Pages/library';
import about from "../../images/about.svg";
import clear from "../../images/clear.svg";
import logoCoronary from "../../images/coronary-flow-logo-dark.png";
import logoBiome from "../../images/logo-dark.png";
import { formatDecimal } from "../../helpers/utils";

function VesselPrint({ vessel, id }) {

    const printCamera = useSelector(item => item.flowviewer.printCamera);
    const viewFFR = useSelector(item => item.flowviewer.viewFFR);
    const _vesselJSON = useSelector(item => item.flowviewer.vesselJSON);
    const vesselJSON = _vesselJSON.find(v => v.vessel == vessel.toLowerCase());
    const uploadedVessels = useSelector(item => item.flowviewer.uploadedVessels);
    const files = useSelector(item => item.flowviewer.files);

    const studyInfo = useSelector(item => item.flowviewer.studyInfo);
    const langs = useSelector(item => item.flowviewer.printLang);
    const pinn = useSelector(item => item.flowviewer.supplementary.pinn);
    const handlerImages = useSelector(item => item.flowviewer.handlerImages);
    const segments = useMemo(() => handlerImages[vessel].map(_ => _.segment).sort().join(', '), [handlerImages]);
    const [clearBtn, setClearBtn] = useState(false);

    function getMaxDiameter() {
        let maxD = 6;
        try {
            maxD = uploadedVessels[vessel].reduce((acc, cur) => {
                return Math.max(acc, cur.r);
            }, 0);
            maxD *= 2;
        } finally {
            return maxD > 6 ? maxD : 6;
        }
    }

    const [draw, setDraw] = useState(false);
    const [startLineBtn, setStartLineBtn] = useState(false);
    const [stoptLineBtn, setStopLineBtn] = useState(false);
    const [jpg4Base64, setJpg4Base64] = useState(false);

    const setThisDraw = () => {
        if (draw) {
            setDraw(false);
            clearCanvas();
        } else {
            setDraw(true);
            setStartLineBtn(true);
        }
    };

    const setCoord = (e) => {
        const pageX = e.nativeEvent.offsetX;
        const pageY = e.nativeEvent.offsetY;

        if (startLineBtn) {
            setStartLineBtn(false);
            setStopLineBtn(true);
            setDraw(true);
            let canvas = document.getElementById(`lines-${id}`);
            let ctx = canvas.getContext('2d');
            ctx.arc(pageX, pageY, 0.1, 0, 2 * Math.PI);
            ctx.strokeStyle = "black";
            ctx.lineWidth = "1";
            ctx.stroke();
        } else if (stoptLineBtn) {
            setStopLineBtn(false);
            setStartLineBtn(false);
            setClearBtn(true);
            setDraw(true);
            let canvas = document.getElementById(`lines-${id}`);
            let ctx = canvas.getContext('2d');
            ctx.arc(pageX, pageY, 0.1, 0, 2 * Math.PI);
            ctx.arc(pageX + 30, pageY, 0.1, 0, 2 * Math.PI);
            ctx.strokeStyle = "black";
            ctx.lineWidth = "1";
            ctx.stroke();
            ctx.font = "bold 16px Arial";
            ctx.fillText(formatDecimal(viewFFR[vessel].ffr), pageX, pageY + 15);
        }
    };

    const clearCanvas = () => {
        let canvas = document.getElementById(`lines-${id}`);
        let ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setStartLineBtn(true);
        setClearBtn(false);
    };

    const [currentLangs, setCurrentLangs] = useState(false);

    const getLangs = () => {
        setCurrentLangs(lang[langs]);
    };

    useEffect(() => {
        getLangs();
    }, [langs]);

    function loadBase64Image(number, callback) {
        if (files["image_" + vessel + "_" + number]) {
            const blobToBase64 = blob => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                return new Promise(resolve => {
                    reader.onloadend = () => {
                        resolve(reader.result);
                    };
                });
            };
            fetch(files["image_" + vessel + "_" + number])
                .then(res => res.blob())
                .then(blobToBase64)
                .then(res => res.replace('data:application/octet-stream;', 'data:image/png;'))
                .then(res => callback(res))
                .catch((e) => {
                    // TODO: log error
                });
        }
    }

    useEffect(() => {
        loadBase64Image("4", setJpg4Base64);
    }, [files["image_" + vessel + "_4"]]);


    function getVesselMap(vessel) {
        switch (vessel) {
            case "lad":
                return <LAD cls={"print-vessel-map print-vessel-map-lad"} />
            case "rca":
                return <RCA cls={"print-vessel-map print-vessel-map-rca"} />
            case "lcx":
                return <LCX cls={"print-vessel-map  print-vessel-map-lcx"} />
            default:
                return "";
        }
    }

    function getCAD() {
        if (vesselJSON) {
            const D = parseFloat(vesselJSON.stenosis.D);
            if (D < 1) {
                return 0;
            } else if (D < 24) {
                return 1;
            } else if (D < 49) {
                return 2;
            } else if (D < 69) {
                return 3;
            } else if (D < 99) {
                return 4;
            } else if (D <= 100) {
                return 5;
            }
        }
        return "";
    }

    function getComorbidities() {
        const map = [
            ['tobacco_abuse', 'comorbidity_tobacco_abuse'],
            ['diabetes', 'comorbidity_diabetes'],
            ['hypertension', 'comorbidity_hypertension'],
            ['previous_mi_history', 'comorbidity_previous_mi_history'],
            ['previous_pci', 'comorbidity_previous_pci'],
        ];
        const isNA = (flag) => { const v = parseInt(pinn[flag]); return v !== 0 && v !== 1; };
        if (map.every(([flag]) => isNA(flag))) return currentLangs.not_available;
        const active = map
            .filter(([flag]) => parseInt(pinn[flag]) === 1)
            .map(([, key]) => currentLangs[key]);
        return active.length > 0 ? active.join(', ') : currentLangs.comorbidity_none;
    }

    function getPPG() {
        const ppg = uploadedVessels[vessel]?.additional_info?.PPG;
        return ppg !== undefined ? formatDecimal(ppg) : "";
    }

    function getFFR() {
        if (viewFFR[vessel].ffr) {
            return formatDecimal(viewFFR[vessel].ffr);
        } else if (uploadedVessels[vessel].ffr) {
            return formatDecimal(Math.min(...uploadedVessels[vessel].ffr));
        }
        return "";
    }

    const tickFormatter = (tickItem) => {
        return formatDecimal(tickItem);
    }

    function plotLumenDiameter() {
        var result = [];
        for (let i = 0; i < uploadedVessels[vessel].center_line.length; i++) {
            result.push({
                x: parseFloat(uploadedVessels[vessel].center_line[i].d).toFixed(1),
                D: uploadedVessels[vessel].center_line[i].r * 2
            });
        }
        return result;
    }

    function getXAxisTicks(data, ticksCount) {
        var result = [];
        var step = Math.round(data.length / (ticksCount));
        for (let i = 0; i < data.length; i += step) {
            result.push(data[i].x);
        }
        return result;
    };

    const lumenDiameter = plotLumenDiameter();

    return (
        <div className="print-form" id={id}>
            <div className="print-from-wrap first-page">
                <div className="print-header d-flex align-items-start justify-content-between mb-3">
                    <div>
                        <img src={logoCoronary} alt="Coronary Flow Analysis" className="print-header-logo-main" />
                        <div className="print-header-contacts">
                            <span className="contact-phone">+33 (0)1 89 16 85 83</span>
                            <span className="ms-3 contact-mail">info@biome-science.com</span>
                        </div>
                    </div>
                    <img src={logoBiome} alt="Biome Science" className="print-header-logo-biome" />
                </div>

                <div className="d-flex gap-3 mb-3">
                    <div className="patient-info-col w-50 border border-2 border-dark p-2 ps-3">
                        <div><b>{currentLangs.coronary_flow_ref_id}</b>: {studyInfo.case_id || currentLangs.not_available}</div>
                        <div><b>{currentLangs.age}</b>: {pinn.age}</div>
                        <div><b>{currentLangs.sex}</b>: {parseInt(pinn.gender) === 1 ? currentLangs.male : currentLangs.female}</div>
                        <div className="d-flex align-items-center">
                            <div>
                                <div><b>{currentLangs.height}</b>: {pinn.height}</div>
                                <div><b>{currentLangs.weight}</b>: {pinn.weight}</div>
                            </div>
                            <div className="patient-info-brace">
                                <span>&#125;</span>
                                <span style={{ fontSize: "13px" }}> {currentLangs.bmi}= {formatDecimal((pinn.weight / Math.pow(pinn.height / 100, 2)), 1)}</span>
                            </div>
                        </div>
                        <div className="patient-info-multiline"><b>{currentLangs.comorbidities}</b>: {currentLangs && getComorbidities()}</div>
                    </div>
                    <div className="patient-info-col w-50 border border-2 border-dark p-2 ps-3">
                        <div><b>{currentLangs.patient_id}</b>: {studyInfo.patient_id || currentLangs.not_available}</div>
                        <div><b>{currentLangs.report_date}</b>: {studyInfo.report_date || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                        <div><b>{currentLangs.ct_study_date}</b>: {studyInfo.ct_study_date || currentLangs.not_available}</div>
                        <div><b>{currentLangs.institution}</b>: {studyInfo.institution || currentLangs.not_available}</div>
                        <div><b>{currentLangs.manufacturer}</b>: {studyInfo.manufacturer || currentLangs.not_available}</div>
                        <div className="patient-info-multiline"><b>{currentLangs.study_description}</b>: {studyInfo.study_description || currentLangs.not_available}</div>
                    </div>
                </div>

                <div className="params-block mb-4 p-3">
                    <p className="params-block-title mb-4">
                        {currentLangs["full_title_" + vessel] || ""}
                    </p>
                    <div className="d-flex align-items-start gap-4">
                        <div className="wrap-lines w-50 pt-1">
                            <LineParam title={currentLangs.cad_rads_score}
                                value={getCAD() !== "" ? getCAD() : currentLangs.not_available}
                                status={getCAD() > 2 ? "my-text-danger" : "my-text-success"}
                                unit={""}
                                bold={true}
                            />
                            <LineParam title={currentLangs.blood_flow_reserve}
                                value={getFFR() || currentLangs.not_available}
                                status={""}
                                unit={""}
                                bold={true}
                            />
                            <LineParam title={currentLangs.pullback_pressure_gradient}
                                value={getPPG() || currentLangs.not_available}
                                status={""}
                                unit={""}
                                bold={true}
                            />
                            <div className="mb-5"></div>
                            <LineParam title={currentLangs.segment_number}
                                value={segments || currentLangs.not_available}
                                status={""}
                                unit={""}
                                bold={false}
                            />
                            <LineParam title={currentLangs.stenosis_diameter}
                                value={vesselJSON ? parseInt(vesselJSON.stenosis.D) : currentLangs.not_available}
                                status={getCAD() > 2 ? "my-text-danger" : "my-text-success"}
                                unit={vesselJSON ? "%" : ""}
                                bold={false}
                            />
                            <LineParam title={currentLangs.length_of_stenosis}
                                value={vesselJSON ? formatDecimal(vesselJSON.stenosis.length, 1) : currentLangs.not_available}
                                status={""}
                                unit={vesselJSON ? currentLangs.position_unit : ""}
                                bold={false}
                            />
                            <LineParam title={currentLangs.stenosis_area}
                                value={vesselJSON ? parseInt(vesselJSON.stenosis.A) : currentLangs.not_available}
                                status={getCAD() > 2 ? "my-text-danger" : "my-text-success"}
                                unit={vesselJSON ? "%" : ""}
                                bold={false}
                            />
                        </div>
                        <div className="params-block-scene">
                            {
                                viewFFR[vessel].ffr &&
                                <div className="editor-btn-group btn-groups d-flex align-items-center" data-html2canvas-ignore='true'>
                                    <button
                                        onClick={() => setThisDraw()}
                                        className={draw ? "toogle-control active" : "toogle-control me-auto"}>
                                        <img className="w-100" src={about} />
                                    </button>
                                    {
                                        draw &&
                                        <button onClick={() => clearCanvas()}
                                            className={clearBtn ? "toogle-control active me-auto" : "toogle-control me-auto"}>
                                            <img className="w-100" src={clear} />
                                        </button>
                                    }
                                </div>
                            }
                            <ScenePrint
                                vessel={vessel}
                                classes={"print-scenes"}
                                maps={"rainbowInvert"}
                                help={false}
                                orbit={true}
                                clearColor={"white"}
                                coord={printCamera[vessel]}
                            />
                            <canvas id={`lines-${id}`}
                                width={326}
                                height={250}
                                onClick={(e) => setCoord(e)}
                                className={draw ? "lines active" : "lines"}
                            />
                        </div>
                    </div>
                </div>
                
                <div className="mb-4">
                    <div className="title-images d-flex align-items-center text-center">
                        <p className="mb-2 mt-0 m-auto about-print-text fw-bold w-100">
                            {currentLangs.straightened_vessel}
                        </p>
                    </div>
                    <div className="img-row">
                        {
                            jpg4Base64 ?
                            <div className="print-img-wrapper print-img-wrapper-big print-img-wrapper-no-bg">
                                <img src={jpg4Base64}
                                    alt={currentLangs.straightened_vessel} /> 
                            </div>
                            : 
                            <div className="print-img-wrapper print-img-wrapper-big"></div>
                        }
                    </div>
                </div>
                <div>
                    <div className="title-images d-flex align-items-center text-center">
                        <p className="mb-2 mt-0 m-auto about-print-text fw-bold">{currentLangs.position}</p>
                    </div>
                    <div className="print-chart">
                        <div className="about-top-line">
                            {currentLangs.position_title}
                        </div>
                        <LineChart width={709}
                            height={160}
                            data={lumenDiameter}
                            margin={{ top: 10, right: 0, bottom: 5, left: -15 }}>
                            <CartesianGrid stroke="#2b2b2b" strokeDasharray="3 3" />
                            <Line type="monotone" dataKey="D" stroke="#0d6efd" dot={{ r: 0 }} />
                            <XAxis dataKey="x"
                                ticks={getXAxisTicks(lumenDiameter, 14)}
                                tickFormatter={tickFormatter}
                                tick={{ fontSize: 13 }}
                            />
                            <YAxis domain={[0, getMaxDiameter()]}
                                tickCount={7}
                                interval={0}
                                tickFormatter={tickFormatter}
                                tick={{ fontSize: 13 }}
                            />
                        </LineChart>
                        <div className="about-bottom-line">
                            {currentLangs.position_unit}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VesselPrint;